import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'utils/with-i18next';
import { useInView } from 'lib/useInView';

const TUTORIAL_TABS = [
  {
    id: 'browse',
    label: 'Browse',
    sectionKey: '2',
    videoSrc: 'https://xcllktuylszpllbsjgkn.supabase.co/storage/v1/object/public/bgms/home/browse.mp4',
  },
  {
    id: 'player',
    label: 'Player',
    sectionKey: '3',
    videoSrc: 'https://xcllktuylszpllbsjgkn.supabase.co/storage/v1/object/public/bgms/home/player.mp4',
  },
  {
    id: 'control',
    label: 'Control',
    sectionKey: '4',
    videoSrc: 'https://xcllktuylszpllbsjgkn.supabase.co/storage/v1/object/public/bgms/home/control.mp4',
    hasExtraText: true,
  },
  {
    id: 'share',
    label: 'Share',
    sectionKey: '5',
    videoSrc: 'https://xcllktuylszpllbsjgkn.supabase.co/storage/v1/object/public/bgms/home/share.mp4',
  },
];

const TutorialSection = ({ t }) => {
  const [activeTab, setActiveTab] = useState('browse');
  const currentTab = TUTORIAL_TABS.find(tab => tab.id === activeTab);
  const [ref, isInView] = useInView({ threshold: 0.2 });

  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-gray-900/50 to-background" ref={ref}>
      <div className={`max-w-6xl mx-auto px-6 md:px-12 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gradient-to-br from-gray-900/90 to-gray-900/50 rounded-xl p-1.5 border border-gray-800 backdrop-blur-sm shadow-xl">
            {TUTORIAL_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-semibold rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-primary to-primary/90 text-black shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Box */}
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16 bg-gradient-to-br from-gray-900/70 to-gray-900/30 rounded-2xl p-8 md:p-12 border border-gray-800 shadow-2xl backdrop-blur-sm">
          {/* Left: Video */}
          <div className="flex-[1.5] w-full">
            <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl p-2 border border-gray-700/50">
              <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                <video
                  key={currentTab.id}
                  className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-2xl shadow-black/50"
                  loop
                  muted
                  playsInline
                  autoPlay
                >
                  <source src={currentTab.videoSrc} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>

          {/* Right: Text */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 whitespace-pre-line leading-tight">
              {t(`section.${currentTab.sectionKey}.h2`)}
            </h2>
            <p className="text-gray-400 text-lg whitespace-pre-line leading-relaxed">
              {t(`section.${currentTab.sectionKey}.p`)}
            </p>
            {currentTab.hasExtraText && (
              <p className="text-gray-400 text-lg whitespace-pre-line mt-4 leading-relaxed">
                {t(`section.${currentTab.sectionKey}.p1`)}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

TutorialSection.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('index')(TutorialSection);
